import { useRef, useEffect } from "react";
import { TextField as MUITextField, styled } from "@mui/material";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import "./activity-tag.scss";
import images from "../../assets/images";
import Avatar from "../avatar/avatar";
import Accordion from "../accordion/accordion";
import { useAuth } from '@erpsquad/common/runtime';
import formatText from "../../utils/format-text";
import Typography from "../typography/typography";

const StyledTextField = styled(MUITextField)(({ theme: { palette } }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: '2rem',
    "& fieldset": {
      borderColor: palette.theme?.secondary[300],
      borderWidth: 2
    },
    "&:hover fieldset": {
      borderColor: palette.theme?.secondary[300],
    },
    "&.Mui-focused fieldset": {
      // borderWidth: "0.0625rem",
      // borderStyle: "solid",
      borderColor: palette.theme?.primary[700],
    },
    "&.Mui-error fieldset": {
      borderColor: palette.theme?.error[600],
    },
  },
}));

export const ActivityTag = (props: any) => {
  const {
    type,
    data,
    placeholder,
    error = false,
    textfield,
    textfieldTitle,
    onCommentSubmit,
    onChange,
    containerProps,
    accordion = true,
    ...rest
  } = props;
  const { user } = useAuth()

  const chatRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [data]);

  const formatDateTime = (dateTime: string) => {
    const currentDate = new Date();
    const commentDate = new Date(dateTime);
    const timeDifference = currentDate.getTime() - commentDate.getTime();
    const secondsDifference = Math.floor(timeDifference / 1000);
    const minutesDifference = Math.floor(secondsDifference / 60);
    const hoursDifference = Math.floor(minutesDifference / 60);

    if (secondsDifference < 60) {
      return `${secondsDifference} second${secondsDifference !== 1 ? 's' : ''
        } ago`;
    } else if (minutesDifference < 60) {
      return `${minutesDifference} minute${minutesDifference !== 1 ? 's' : ''
        } ago`;
    } else if (hoursDifference < 24) {
      return `${hoursDifference} hour${hoursDifference !== 1 ? 's' : ''} ago`;
    } else {
      // Format the date in the desired format
      const optionsDate: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      };

      const optionsTime: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: 'numeric',
        hour12: false
      };

      const formattedDate = commentDate.toLocaleString('en-US', optionsDate);
      const formattedTime = commentDate.toLocaleString('en-US', optionsTime);

      return `${formattedDate} • ${formattedTime}`;
    }
  };

  const handleKeyPress = (event: any) => {
    if (event.key === 'Enter') {
      const commentValue = (event.target as HTMLInputElement).value;
      if (commentValue.trim() !== '') {
        onCommentSubmit(commentValue);
        scrollToBottom();
      }
    }
  };

  const timelineItems =
    type === "activity" ? (
      data?.map((item: any, index: number) => (
        <div className="Timeline--Activity">
          <TimelineItem key={index}>
            <TimelineSeparator>
              <TimelineDot />
              {data.length - 1 !== index && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent className="Timeline--Content">
              <Typography type="s4" color="theme.secondary.800" className="Timeline--ActivityLogItem">
                {item.message} · {formatDateTime(item.created_at)}
              </Typography>
            </TimelineContent>
          </TimelineItem>
        </div>
      ))
    ) : (
      <>
        <div className="Timeline--Chat" ref={chatRef}>
          {data.map((item: any, index: number) => (
            <TimelineItem key={index}>
              <TimelineSeparator>
                <Avatar src={images.chat} size={17} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                {item.name} commented this · {formatDateTime(item.created_at)}
                <div className="Timeline--CommentWrap">
                  <p className="Timeline--Comment">{item.message}</p>
                </div>
              </TimelineContent>
            </TimelineItem>
          ))}
        </div>
      </>
    );

  // const avatarStyle = {
  //   borderRadius: '0.5rem',
  //   padding: '0.875rem 1.28125rem',
  //   textAlign: 'center',
  //   backgroundColor: `${randomColor}.200`,
  //   color: `${randomColor}.1000`
  // };

  return (
    // <Box position='relative'>
    <div className="ActivityContainer" {...containerProps}>
      <Timeline className="Timeline">
        {accordion ? 
          <Accordion title='Activities' accordionProps={{ defaultExpanded: true }}>
          {timelineItems}
        </Accordion> : 
        <div className="Timeline--ActivityBox">
          {timelineItems}
        </div>
        }
        {textfield && (
          <div className="Timeline--CommentBox">
            {textfieldTitle}
            <div className="Timeline--CommentText">
              <Avatar size={32} alt={formatText(user?.full_name)} className="avatar-class" />
              <StyledTextField
                error={error}
                placeholder={placeholder}
                className="textfield"
                onKeyDown={handleKeyPress}
                onChange={onChange}
                {...rest}
              />
            </div>
          </div>
        )}
      </Timeline>
    </div>
    // </Box>
  );
};

export default ActivityTag;
